/**
 * locationSettingsLink — the query shape that names one tab of a location's
 * settings dialog:
 *
 *   ?view=dashboard:location&acc=<account>&co=<location>&tab=time-attendance&subtab=geofence
 *
 * (the geofence email / magic link). `tab` is required — without it the
 * link is the plain location dashboard and nothing opens; `subtab` is
 * optional. Legacy tab ids resolve where the LocationModal resolves them.
 *
 * The dialog can be open over ANY view (the Executive dashboard, Platform
 * Administration, a ⌘K jump). Off the location dashboard the same link adds
 * `settings=location`, because there `tab` alone is ambiguous — Reports uses
 * `tab=invoices` for its own tabs:
 *
 *   ?view=dashboard:executive&acc=<account>&co=<location>&settings=location&tab=details
 *
 * The open dialog writes this shape itself (withLocationSettingsLink), so the
 * address bar always says which location and tab are showing, Back closes the
 * dialog, and a reload opens it again.
 */
export interface LocationSettingsLink {
  locationId: string;
  accountId?: string;
  tab: string;
  subTab?: string;
}

/** `settings=location`: the dialog is named by tab / subtab on a view other than the location dashboard. */
export const LOCATION_SETTINGS_MARKER = 'location';

const toParams = (search: string | URLSearchParams): URLSearchParams =>
  typeof search === 'string' ? new URLSearchParams(search.replace(/^[?&]/, '')) : search;

export function parseLocationSettingsLink(search: string | URLSearchParams): LocationSettingsLink | null {
  const params = toParams(search);
  if (params.get('view') !== 'dashboard:location' && params.get('settings') !== LOCATION_SETTINGS_MARKER) return null;
  const locationId = params.get('co')?.trim();
  const tab = params.get('tab')?.trim();
  if (!locationId || !tab) return null;
  const subTab = params.get('subtab')?.trim() || undefined;
  const accountId = params.get('acc')?.trim() || undefined;
  return { locationId, accountId, tab, subTab };
}

/** Whether the URL names this location's settings dialog (any tab). */
export function describesLocationSettings(search: string | URLSearchParams, locationId: string): boolean {
  return parseLocationSettingsLink(search)?.locationId === locationId;
}

/**
 * The same URL naming this location's settings on this tab. Everything else
 * in the query (the view, a work order, the dashboard's own state) is kept,
 * so closing the dialog can hand the page back exactly as it was.
 */
export function withLocationSettingsLink(search: string, link: LocationSettingsLink): string {
  const params = toParams(search);
  params.set('settings', LOCATION_SETTINGS_MARKER);
  if (link.accountId) params.set('acc', link.accountId);
  params.set('co', link.locationId);
  params.set('tab', link.tab);
  if (link.subTab) params.set('subtab', link.subTab);
  else params.delete('subtab');
  return `?${params.toString()}`;
}

/** The same URL without the settings target (the modal closed; back/reload should not reopen it). */
export function stripLocationSettingsLink(search: string): string {
  const params = toParams(search);
  params.delete('tab');
  params.delete('subtab');
  params.delete('settings');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}
